import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  MagnifyingGlass, SortAscending, SortDescending, DownloadSimple, Trash, Eye,
  EnvelopeSimple, Phone, UsersThree, CalendarCheck, TrendUp,
} from "@phosphor-icons/react";
import { Checkbox } from "../ui/checkbox";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../ui/alert-dialog";
import LeadDetailDrawer from "./LeadDetailDrawer";

const TYPES = ["all", "quiz", "contact", "booking", "corporate", "chatbot"];
const STATUSES = ["all", "new", "contacted", "converted", "closed"];
const STATUS_STYLES = {
  new: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  contacted: "bg-blue-500/15 text-blue-400 border-blue-500/40",
  converted: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  closed: "bg-zinc-500/15 text-zinc-400 border-zinc-500/40",
};

function csvEscape(value) {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("recent");
  const [selectedIds, setSelectedIds] = useState([]);
  const [drawerLeadId, setDrawerLeadId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    const params = {};
    if (typeFilter !== "all") params.type = typeFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    if (search.trim()) params.search = search.trim();
    api.get("/admin/leads", { params }).then(({ data }) => setLeads(data)).catch(() => {});
  };

  const loadStats = () => api.get("/admin/leads/stats").then(({ data }) => setStats(data)).catch(() => {});

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, statusFilter, search]);

  const refreshAll = () => {
    load();
    loadStats();
  };

  const sortedLeads = [...leads].sort((a, b) => {
    const diff = new Date(a.created_at) - new Date(b.created_at);
    return sortOrder === "recent" ? -diff : diff;
  });

  const toggleSelect = (id) => setSelectedIds((ids) => (ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]));
  const toggleSelectAll = () =>
    setSelectedIds((ids) => (ids.length === sortedLeads.length ? [] : sortedLeads.map((l) => l.id)));

  const quickStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Lead updated");
      refreshAll();
    } catch {
      toast.error("Failed to update lead");
    }
  };

  const confirmDelete = async () => {
    try {
      if (Array.isArray(deleteTarget)) {
        await api.delete("/admin/leads", { data: { ids: deleteTarget } });
        toast.success(`${deleteTarget.length} lead(s) deleted`);
        setSelectedIds([]);
      } else {
        await api.delete(`/admin/leads/${deleteTarget}`);
        toast.success("Lead deleted");
      }
      setDeleteTarget(null);
      refreshAll();
    } catch {
      toast.error("Failed to delete lead(s)");
    }
  };

  const exportCsv = () => {
    const header = ["Name", "Email", "Phone", "Type", "Status", "Requested Time", "Created At"];
    const rows = sortedLeads.map((l) => [l.name, l.email, l.phone || "", l.type, l.status, l.preferred_datetime || "", l.created_at]);
    const csv = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `existfit-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-leads-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-3xl text-white tracking-tight">Leads & Renewals</h1>
        <button data-testid="export-leads-csv-button" onClick={exportCsv} className="flex items-center gap-2 text-sm border border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37]/60 hover:text-white rounded-full px-4 py-2 transition-colors">
          <DownloadSimple size={16} /> Export CSV
        </button>
      </div>

      {stats && (
        <div data-testid="leads-stats-bar" className="grid sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
              <UsersThree size={20} className="text-amber-400" />
            </div>
            <div>
              <p data-testid="stat-new-uncontacted" className="font-heading text-2xl text-white">{stats.new_uncontacted}</p>
              <p className="text-xs text-[#71717A]">Uncontacted New Leads</p>
            </div>
          </div>
          <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
              <CalendarCheck size={20} className="text-[#D4AF37]" />
            </div>
            <div>
              <p data-testid="stat-bookings-scheduled" className="font-heading text-2xl text-white">{stats.bookings_scheduled}</p>
              <p className="text-xs text-[#71717A]">Bookings Scheduled</p>
            </div>
          </div>
          <div className="bg-[#141414] border border-[#27272A] rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
              <TrendUp size={20} className="text-emerald-400" />
            </div>
            <div>
              <p data-testid="stat-top-channel" className="font-heading text-2xl text-white capitalize">{stats.top_channel || "-"}</p>
              <p className="text-xs text-[#71717A]">Top Converting Channel</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input
            data-testid="leads-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email or phone..."
            className="w-full bg-[#141414] border border-[#27272A] rounded-full pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-[#71717A]"
          />
        </div>
        <button
          data-testid="leads-sort-toggle"
          onClick={() => setSortOrder((s) => (s === "recent" ? "oldest" : "recent"))}
          className="flex items-center gap-2 text-sm border border-[#27272A] text-[#A1A1AA] hover:border-[#D4AF37]/60 hover:text-white rounded-full px-4 py-2.5 transition-colors shrink-0"
        >
          {sortOrder === "recent" ? <SortDescending size={16} /> : <SortAscending size={16} />}
          {sortOrder === "recent" ? "Most Recent First" : "Oldest First"}
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex gap-2 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`lead-status-filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs capitalize border transition-colors ${
                statusFilter === s ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#27272A] text-[#A1A1AA] hover:border-white/30"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          {TYPES.map((t) => (
            <button
              key={t}
              data-testid={`lead-filter-${t}`}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 rounded-full text-xs capitalize border transition-colors ${
                typeFilter === t ? "bg-white text-black border-white" : "border-[#27272A] text-[#A1A1AA] hover:border-white/30"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div data-testid="leads-bulk-action-bar" className="flex items-center justify-between bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-2xl px-5 py-3 mb-4">
          <p className="text-sm text-white">{selectedIds.length} lead(s) selected</p>
          <button
            data-testid="bulk-delete-button"
            onClick={() => setDeleteTarget(selectedIds)}
            className="flex items-center gap-2 text-sm bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-full px-4 py-2 transition-colors"
          >
            <Trash size={16} /> Delete Selected
          </button>
        </div>
      )}

      <div className="bg-[#141414] border border-[#27272A] rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead className="text-[#71717A] text-xs uppercase tracking-wide border-b border-[#27272A]">
            <tr>
              <th className="px-5 py-3 w-10">
                <Checkbox
                  data-testid="select-all-leads-checkbox"
                  checked={sortedLeads.length > 0 && selectedIds.length === sortedLeads.length}
                  onCheckedChange={toggleSelectAll}
                />
              </th>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Type</th>
              <th className="text-left px-5 py-3">Contact</th>
              <th className="text-left px-5 py-3">Requested Time</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedLeads.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-[#27272A] last:border-0">
                <td className="px-5 py-3">
                  <Checkbox
                    data-testid={`lead-select-checkbox-${l.id}`}
                    checked={selectedIds.includes(l.id)}
                    onCheckedChange={() => toggleSelect(l.id)}
                  />
                </td>
                <td className="px-5 py-3 text-white cursor-pointer" onClick={() => setDrawerLeadId(l.id)}>
                  {l.name}
                </td>
                <td className="px-5 py-3 text-[#A1A1AA] capitalize">{l.type}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <a data-testid={`quick-email-${l.id}`} href={`mailto:${l.email}`} title={l.email} className="text-[#71717A] hover:text-[#D4AF37] transition-colors">
                      <EnvelopeSimple size={16} />
                    </a>
                    {l.phone && (
                      <a data-testid={`quick-call-${l.id}`} href={`tel:${l.phone}`} title={l.phone} className="text-[#71717A] hover:text-[#D4AF37] transition-colors">
                        <Phone size={16} />
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3 text-[#D4AF37] whitespace-nowrap">{l.preferred_datetime || "-"}</td>
                <td className="px-5 py-3">
                  <select
                    data-testid={`lead-status-select-${l.id}`}
                    value={l.status}
                    onChange={(e) => quickStatus(l.id, e.target.value)}
                    className={`border rounded-full text-xs px-2.5 py-1.5 font-semibold capitalize bg-[#0A0A0A] ${STATUS_STYLES[l.status] || STATUS_STYLES.new}`}
                  >
                    {STATUSES.filter((s) => s !== "all").map((s) => (
                      <option key={s} value={s} className="bg-[#141414] text-white">
                        {s}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <button data-testid={`view-lead-${l.id}`} onClick={() => setDrawerLeadId(l.id)} className="text-[#71717A] hover:text-white transition-colors">
                      <Eye size={16} />
                    </button>
                    <button data-testid={`delete-lead-${l.id}`} onClick={() => setDeleteTarget(l.id)} className="text-[#71717A] hover:text-red-400 transition-colors">
                      <Trash size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!sortedLeads.length && (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-[#71717A]">
                  No leads match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {drawerLeadId && (
        <LeadDetailDrawer leadId={drawerLeadId} onClose={() => setDrawerLeadId(null)} onChanged={refreshAll} />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-lead-confirm-dialog" className="bg-[#141414] border-[#27272A] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading">Delete Lead(s)?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              Are you sure you want to delete {Array.isArray(deleteTarget) ? deleteTarget.length : 1} lead(s)? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-lead-cancel-button" className="bg-transparent border border-white/20 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction data-testid="delete-lead-confirm-button" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
